/**
 * Founsi AI — Semantic theme
 *
 * Values are composed from `./tokens.ts` (single source of truth for the
 * brand palette). Keys are preserved from the legacy theme so the 60+
 * components consuming `theme.colors.*` continue to work unchanged — only
 * the underlying values shift to the Founsi AI light brand.
 *
 * To swap the brand palette later, edit `tokens.ts`. Do not hardcode hex
 * values anywhere downstream.
 */
import { brand, grey, status, surfaces, content, badgeColors, rankColors, fp } from './tokens';

/**
 * Fidelity Purple semantic export — used by screens on the new design system.
 * Screens not yet migrated continue to consume the legacy `colors` export below.
 */
export const fpColors = fp;

export const colors = {
  // Primary brand
  primary: brand.purple1,          // #9378FF
  secondary: brand.purple4,        // subtle tint surface
  accent: brand.purple2,           // stronger tint

  // Background and surfaces (light mode)
  background: surfaces.app,        // grey[50] — app background
  surface: surfaces.card,          // #FFFFFF — card background
  text: content.primary,           // grey[500]
  textMuted: content.muted,        // grey[200]

  // Status
  success: status.success,
  warning: status.warning,
  error: status.error,
  info: status.info,

  // Status backgrounds (new, additive — safe to add)
  successBg: status.successBg,
  warningBg: status.warningBg,
  errorBg: status.errorBg,
  infoBg: status.infoBg,

  // Grayscale (remapped to light ramp)
  gray50: grey[50],
  gray100: grey[50],     // collapsed — background tier
  gray200: grey[100],    // dividers
  gray300: grey[100],    // borders
  gray400: grey[200],    // icons / muted
  gray500: grey[300],    // secondary text
  gray600: grey[500],    // primary text

  // Common convenience
  white: '#FFFFFF',
  black: '#000000',
  border: grey[100],
  textSecondary: content.secondary,
  primaryLight: brand.purple3,     // lighter brand tint for emphasis surfaces

  // Alpha colors
  overlay: surfaces.overlay,
  cardBg: 'rgba(255, 255, 255, 0.96)',

  // Legacy accent hues (still referenced by some components — keep but
  // retune to brand-adjacent values so nothing looks jarring).
  teal: badgeColors.teal,
  purple: brand.purple1,
  pink: badgeColors.pink,

  // Interactive states
  divider: 'rgba(52, 48, 58, 0.10)',       // grey[500] @ 10%
  hover: 'rgba(147, 120, 255, 0.08)',      // brand.purple1 @ 8%
  active: 'rgba(147, 120, 255, 0.16)',     // brand.purple1 @ 16%

  // Sidebar (remapped to light surfaces + brand accent)
  sidebarBackground: '#FFFFFF',
  sidebarForeground: content.primary,
  sidebarPrimary: brand.purple1,
  sidebarAccent: brand.purple4,
  sidebarBorder: grey[100],
  sidebarRing: brand.purple2,

  // Popover + shadcn-style semantic slots
  popover: '#FFFFFF',
  popoverForeground: content.primary,
  primaryForeground: content.onPrimary,    // white on purple primary
  secondaryForeground: brand.purple0,      // strong brand text on tint surface
  muted: brand.purple4,
  destructiveForeground: '#FFFFFF',
  ring: brand.purple1,

  // Chart colors — stepped brand purples + a neutral
  chart1: brand.purple1,
  chart2: brand.purple0,
  chart3: brand.purple2,
  chart4: brand.purple3,
  chart5: grey[300],

  // New: exposed badge/rank maps for BadgeDisplay + kin
  badges: badgeColors,
  ranks: rankColors,
};

export const fonts = {
  inter: {
    regular: 'Inter_400Regular',
    semiBold: 'Inter_600SemiBold',
    bold: 'Inter_700Bold',
  },
  poppins: {
    regular: 'Poppins_400Regular',
    medium: 'Poppins_500Medium',
    semiBold: 'Poppins_600SemiBold',
    bold: 'Poppins_700Bold',
  },
  roboto: {
    regular: 'Roboto_400Regular',
  },
  hollaMediaeval: {
    regular: 'HollaMediaeval_400Regular',
    bold: 'HollaMediaeval_700Bold',
  },
  robotoMono: {
    thin: 'RobotoMono_100Thin',
    light: 'RobotoMono_300Light',
    regular: 'RobotoMono_400Regular',
    medium: 'RobotoMono_500Medium',
    bold: 'RobotoMono_700Bold',
  },
};

export const fontSizes = {
  xs: 12,
  sm: 14,
  md: 16,
  base: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30,
  '4xl': 36,
  '5xl': 48,
  '6xl': 60,
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64,
};

export const borderRadius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
};

// Shadows — retuned for light surfaces (softer, more ambient).
export const shadows = {
  sm: {
    shadowColor: grey[500],
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 2,
  },
  md: {
    shadowColor: grey[500],
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 4,
  },
  lg: {
    shadowColor: grey[500],
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 20,
    elevation: 10,
  },
};

export const theme = {
  colors,
  fonts,
  fontSizes,
  spacing,
  borderRadius,
  shadows,
};

export type Theme = typeof theme;
export default theme;
